import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authApi, type NotificationItem, type NotificationsFeed } from '../services/authApi';
import { useJobWatcher } from '../contexts/JobWatcherContext';
import NotificationsPanel from './NotificationsPanel';

const POLL_MS = 5 * 60_000;

export default function NotificationsBell() {
  const navigate = useNavigate();
  const watcher = useJobWatcher();
  const [feed, setFeed] = useState<NotificationsFeed | null>(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [lastRunning, setLastRunning] = useState<Record<string, unknown>[] | null>(null);
  const inFlight = useRef(false);
  const readSeq = useRef(0);
  const bellRef = useRef<HTMLButtonElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const seq = readSeq.current;
    try {
      const next = await authApi.getNotifications();
      // A read action since this request started makes the response stale.
      if (seq === readSeq.current) {
        setFeed(next);
        setError(false);
      }
    } catch {
      setError(true);
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        bellRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const markItemRead = (item: NotificationItem) => {
    if (item.read) return;
    readSeq.current += 1;
    setFeed((f) => f && {
      ...f,
      items: f.items.map((i) => (i.key === item.key ? { ...i, read: true } : i)),
      unread_count: Math.max(0, f.unread_count - 1),
    });
    // Failure is silent: the next fetch reconciles.
    authApi.markNotificationsRead({ items: [item.key] }).catch(() => {});
  };

  const markAllRead = async (): Promise<boolean> => {
    readSeq.current += 1;
    const snapshot = feed;
    setFeed((f) => f && { ...f, items: f.items.map((i) => ({ ...i, read: true })), unread_count: 0 });
    try {
      const res = await authApi.markNotificationsRead({ all: true });
      setFeed((f) => f && { ...f, unread_count: res.unread_count });
      return true;
    } catch {
      setFeed(snapshot);
      return false;
    }
  };

  const toggle = () => {
    if (!open) void refresh();
    setOpen((o) => !o);
  };

  const unread = feed?.unread_count ?? 0;
  const running = (watcher?.running.length ?? 0) > 0;

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={bellRef}
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="relative flex items-center justify-center w-8 h-8 rounded-ctl text-ink-2 hover:bg-accent-tint"
      >
        <Bell className="w-4 h-4" aria-hidden="true" />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[16px] px-1 rounded-[8px] bg-signal text-white text-[10px] leading-4 text-center"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
        {running && (
          <span
            data-testid="notif-running-dot"
            className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-accent"
          />
        )}
      </button>
      {open && (
        <NotificationsPanel
          feed={feed}
          error={error}
          onRetry={() => void refresh()}
          onItemAction={(item, path) => { markItemRead(item); go(path); }}
          onMarkAllRead={markAllRead}
          onNavigate={go}
          initialRunning={lastRunning}
          onRunningLoaded={setLastRunning}
        />
      )}
    </div>
  );
}
